import { makeVar, useReactiveVar } from '@apollo/client'
import { VIEWER_MODE_STORAGE_KEY } from 'config'

export enum ViewerMode {
  USER = 'USER',
  SYSTEM_ADMIN = 'SYSTEM_ADMIN',
}

const VIEWER_MODE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365

const getCookie = (name: string) => {
  if (typeof document === 'undefined') return null

  const prefix = `${encodeURIComponent(name)}=`
  const cookie = document.cookie
    .split('; ')
    .find(value => value.startsWith(prefix))

  return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null
}

const setCookie = (name: string, value: string) => {
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''

  document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(
    value
  )}; Path=/; Max-Age=${VIEWER_MODE_COOKIE_MAX_AGE}; SameSite=Lax${secure}`
}

const removeCookie = (name: string) => {
  document.cookie = `${encodeURIComponent(
    name
  )}=; Path=/; Max-Age=0; SameSite=Lax`
}

const getInitialViewerMode = (): ViewerMode => {
  const storedMode = getCookie(VIEWER_MODE_STORAGE_KEY)

  return storedMode === ViewerMode.SYSTEM_ADMIN
    ? ViewerMode.SYSTEM_ADMIN
    : ViewerMode.USER
}

export const viewerModeVar = makeVar<ViewerMode>(getInitialViewerMode())

export const viewerCanUseSystemAdminModeVar = makeVar<boolean | null>(null)

export const isSystemAdminMode = () =>
  viewerModeVar() === ViewerMode.SYSTEM_ADMIN

export const setViewerMode = (mode: ViewerMode) => {
  setCookie(VIEWER_MODE_STORAGE_KEY, mode)
}

export const resetViewerMode = () => {
  viewerModeVar(ViewerMode.USER)
  viewerCanUseSystemAdminModeVar(false)

  removeCookie(VIEWER_MODE_STORAGE_KEY)
}

// The page is reloaded, as the data loaded in the system admin mode stays in
// the cache. Only the cookie is cleared, so the page isn't rendered in the user
// mode with that data before the reload.
export const exitSystemAdminMode = (redirectPath?: string) => {
  removeCookie(VIEWER_MODE_STORAGE_KEY)

  if (redirectPath) {
    window.location.assign(redirectPath)
  } else {
    window.location.reload()
  }
}

export const setViewerCanUseSystemAdminMode = (
  viewerCanUseSystemAdminMode?: boolean | null
) => {
  if (viewerCanUseSystemAdminMode == null) return

  if (!viewerCanUseSystemAdminMode) {
    resetViewerMode()
    return
  }

  viewerCanUseSystemAdminModeVar(true)
}

export const useViewerMode = () => {
  const mode = useReactiveVar(viewerModeVar)

  return {
    mode,
    setMode: setViewerMode,
    resetMode: resetViewerMode,
  }
}

export const useIsSystemAdminMode = () =>
  useReactiveVar(viewerModeVar) === ViewerMode.SYSTEM_ADMIN
